/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/**
 * FuzzyVRM.js
 * @description Main component for the Fuzzy VRM (Vehicle Registration Mark) feature.
 * Orchestrates all sub-components including the header, grid, and image dialog.
 * Manages page-level state such as pagination and image dialog visibility.
 *
 * @component
 * @example
 * const caseId = '12345'
 * return <FuzzyVRM preCaseId={caseId} />
 */
import React from 'react'
import './FuzzyVRM.css'
import { useNavigate } from 'react-router-dom'
import { FuzzyVRMHeader, FuzzyVRMGrid, FuzzyVRMImageDialog } from './components'
import { useImageDialog } from './hooks'
import { FUZZY_VRM_STATIC_CODE, DEFAULT_PAGE_SIZE } from './constants'
import { observationDetailsAtom } from '@components/recoil/atoms/atoms'
import { useRecoilValue } from 'recoil'

/**
 * @param {Object} props - Component props
 * @param {string} props.preCaseId - The ID of the pre-case to load data for
 * @returns {JSX.Element} The rendered FuzzyVRM component
 */
const FuzzyVRM = ({ preCaseId }) => {
  const gridRef = React.useRef()
  const navigate = useNavigate()

  const [perPageSize, setPerPageSize] = React.useState(DEFAULT_PAGE_SIZE)
  const [selectedRowImages, setSelectedRowImages] = React.useState([])
  const [selectedRowVrmNo, setSelectedRowVrmNo] = React.useState('')
  const { isOpen, activeImageIndex, onOpen, onClose, onChangeIndex } =
    useImageDialog()
  const observationDetails = useRecoilValue(observationDetailsAtom)

  const handleBack = () => navigate('/manualreviewpage')

  const handleViewImage = (params) => {
    const attachments = params?.data?.attachments || []
    setSelectedRowVrmNo(params?.data?.vrmNo || '')
    const formattedImages = attachments.map((att) => ({
      id: att.id,
      name: att.fileName,
      url: att.fileLocation,
      timestamp: att.createdOn,
      type: att.vehicleDirection === 'APPROACHING' ? 'entry' : 'exit',
    }))
    setSelectedRowImages(formattedImages)
    onOpen()
  }

  return (
    <>
      <div className="FuzzyVRM">
        <FuzzyVRMHeader
          title={FUZZY_VRM_STATIC_CODE.pageTitle.replace(
            '__caseNumber__',
            observationDetails?.id || ''
          )}
          onBackClick={handleBack}
        />
        <FuzzyVRMGrid
          gridRef={gridRef}
          preCaseId={preCaseId}
          onViewImageClick={handleViewImage}
          perPageSize={perPageSize}
          onPageSizeChange={setPerPageSize}
        />
      </div>
      <FuzzyVRMImageDialog
        open={isOpen}
        onClose={onClose}
        images={selectedRowImages}
        rowVrmNo={selectedRowVrmNo}
        currentIndex={activeImageIndex}
        onChangeIndex={onChangeIndex}
      />
    </>
  )
}

export default FuzzyVRM
